import { SubNavContainer } from '@/components/sub-nav-container';
import { useStrapi } from '@/hooks/useStrapi';
import { Service } from '@/lib/types';
import { Button } from '@opaz/ui-components';
import { createFileRoute, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { Check, CirclePlus, Clock4Icon, MinusCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/useAnalytics';

export const Route = createFileRoute('/services/services-catalogue/$id')({
  component: RouteComponent,
  context: ({ params }) => {
    return {
      getBreadcrumbData: () => ({
        id: params.id,
        translationKey: null,
      }),
    };
  },
  head: () => {
    return {
      meta: [
        {
          title: 'Service Details - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

export default function RouteComponent() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const analytics = useAnalytics();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  const serviceDetailViewTracked = useRef(false);

  const { id } = useParams({ strict: false });

  const { data, isLoading, error } = useStrapi<Service>(
    'services',
    i18n.language,
    `&filters[documentId][$eq]=${id}&populate=*`,
  );

  const service = data?.[0];
  const authorityId = service?.authority?.documentId;

  // Track service detail view only once when page loads
  if (!serviceDetailViewTracked.current && service && analytics) {
    const serviceTitleEn = service.localizations?.find((l) => l.locale === 'en')?.title || service.title || '';
    analytics.trackServiceDetailView(service.documentId, serviceTitleEn);
    serviceDetailViewTracked.current = true;
  }

  const {
    data: authorty,
    isLoading: authortyIsLoading,
    error: authortyIsError,
  } = useStrapi<Service>('authorities', i18n.language, `&filters[documentId][$eq]=${authorityId}&populate=*`);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Track API errors
  useEffect(() => {
    if (error && analytics) {
      analytics.trackApiError('services', 500);
    }
    if (authortyIsError && analytics) {
      analytics.trackApiError('authorities', 500);
    }
  }, [error, authortyIsError, analytics]);

  const handleStartService = useCallback(() => {
    if (!service) return '';

    if (analytics) {
      analytics.trackServiceApplyClick(service.documentId, pathname);
    }

    // Check if code is a URL (starts with http:// or https://)
    const isCodeUrl = service.code && /^https?:\/\//i.test(service.code);
    const externalUrl = service.url || (isCodeUrl ? service.code : null);

    if (externalUrl) {
      // Navigate to external URL in the same window
      window.location.href = externalUrl;

      // Track external link click
      if (analytics) {
        analytics.trackHandoverSuccess(service.documentId);
      }
      return;
    }

    const zoneCode = service.zones?.[0]?.code || '';

    try {
      navigate({
        to: '/start-service',
        search: {
          srv: service.code,
          zone: zoneCode,
          plotId: undefined,
          lang: i18n.language,
          ref: undefined,
          cr: undefined,
        },
      });

      // Track internal navigation to service form
      if (analytics) {
        analytics.trackServiceApplication(service.documentId);
      }
    } catch (e) {
      if (analytics) {
        analytics.trackHandoverFailure('navigation_error');
      }

      console.error('Navigation error:', e);
    }
  }, [service, analytics, pathname, navigate, i18n.language]);

  const handleFeesToggle = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
  };

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" aria-busy="true" className="flex justify-center items-center mt-14">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        <span className="sr-only">{t('common.loading')}</span>
      </div>
    );
  }

  if (error || !service)
    return (
      <div role="alert" className="flex justify-center mx-auto py-8 text-red-500">
        {t('common.errorMessage')}
      </div>
    );

  return (
    <>
      <SubNavContainer entityTitle={service.title} isLoading={isLoading}>
        <div className="max-w-container-960 pb-8">
          <h1 className="text-3xl font-bold mb-5 text-foreground capitalize">{service.title}</h1>
          <p className="text-xl font-normal text-foreground pb-10">{service.description}</p>
          <div className="flex gap-4 items-center">
            {authortyIsLoading || authortyIsError || !authorty ? (
              <div
                className="w-8 h-8 bg-gray-600 rounded-full text-center text-white flex items-center justify-center font-semibold"
                aria-label={t('serviceDetails.authorityInitial', {
                  initial: service?.authority?.title?.charAt(0) || '?',
                })}
                role="img"
              >
                {service?.authority?.title?.charAt(0) || '?'}
              </div>
            ) : (
              <img
                src="/OPAZ_low_res.png"
                alt={service?.authority?.title}
                className="w-8 h-8 rounded-full object-cover object-center"
                loading="lazy"
              />
            )}
            <p className="font-bold">{service?.authority?.title}</p>
          </div>
        </div>
      </SubNavContainer>

      <main className="max-w-container-960 mx-auto pt-8 pb-14 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-10 text-foreground px-4 lg:px-0">
        <section className="space-y-10" aria-label={t('serviceDetails.mainContentAriaLabel')}>
          {/* Before you start */}
          {service?.beforeYouStart2 && service?.beforeYouStart2?.length > 0 && (
            <article>
              <h2 className="text-2xl font-bold">{t('serviceDetails.beforeStartTitle')}</h2>
              <p className="my-6">{t('serviceDetails.beforeStartNote')}</p>
              <ul className="space-y-2">
                {service?.beforeYouStart2?.map((s) => (
                  <li key={s.id} className="flex items-start gap-2">
                    <Check />
                    <span>{s.step}</span>
                  </li>
                ))}
              </ul>
            </article>
          )}

          {/* Fees Toggle */}
          <section className="my-12" aria-labelledby="fees-title">
            <div className="flex items-center justify-between">
              <h2 id="fees-title" className="text-2xl font-bold">
                {t('serviceDetails.feesTitle')}
              </h2>
              <Button
                variant="ghost"
                className="text-link underline underline-offset-8 hover:bg-white hover:text-link/80 inline-flex items-center gap-1 border-none"
                onClick={handleFeesToggle}
                aria-expanded={isOpen}
                aria-controls="fees-content"
              >
                {isOpen ? (
                  <MinusCircle className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <CirclePlus className="w-4 h-4" aria-hidden="true" />
                )}
                <span>{isOpen ? t('serviceDetails.feesHide') : t('serviceDetails.feesShow')}</span>
              </Button>
            </div>
            {isOpen && (
              <p id="fees-content" className="py-6 text-foreground">
                {service.cost}
              </p>
            )}
            <hr className="mt-3 border-border" />
          </section>

          {/* Steps */}
          <section aria-labelledby="steps-title">
            <h2 id="steps-title" className="text-2xl font-bold mb-6">
              {t('serviceDetails.stepsTitle')}
            </h2>

            <ol className="space-y-6">
              {service?.steps.map((step, i) => (
                <li key={step.id} className="flex items-center gap-3">
                  <div className="flex-none">
                    <span
                      className="w-6 h-6 rounded-full bg-brand text-white text-xs flex items-center justify-center font-semibold mt-1"
                      aria-hidden="true"
                    >
                      {i + 1}
                    </span>
                  </div>
                  <p className="text-sm">{step.step}</p>
                </li>
              ))}
            </ol>

            <hr className="mt-8 border-border" />
          </section>

          {/* Output of services */}
          {service?.OutputOfServices && service?.OutputOfServices?.length > 0 && (
            <section aria-labelledby="output-of-service-title">
              <h2 id="output-of-service-title" className="text-2xl font-bold mb-6">
                {t('serviceDetails.outputOfServicesTitle')}
              </h2>

              <ol className="space-y-6">
                {service?.OutputOfServices?.map((step, i) => (
                  <li key={step.id} className="flex items-center gap-3">
                    <div className="flex-none">
                      <span
                        className="w-6 h-6 rounded-full bg-brand text-white text-xs flex items-center justify-center font-semibold mt-1"
                        aria-hidden="true"
                      >
                        {i + 1}
                      </span>
                    </div>
                    <p className="text-sm">{step.step}</p>
                  </li>
                ))}
              </ol>

              <hr className="mt-8 border-border" />
            </section>
          )}

          {/* Availability */}
          <section aria-labelledby="availability-title" className="mt-12">
            <h2 id="availability-title" className="text-2xl font-bold mb-6">
              {t('serviceDetails.availabilityTitle')}
            </h2>
            {service.zones && service.zones.length > 0 && (
              <ul className="text-foreground space-y-2">
                {service.zones.map((zone) => (
                  <li key={zone.documentId} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-current inline-block" aria-hidden="true" />
                    <span>{zone.title}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </section>

        <aside className="border-s ps-6" aria-label={t('serviceDetails.sidebarAriaLabel')}>
          <h3 className="text-2xl font-bold mb-4">{t('serviceDetails.sidebarTitle')}</h3>
          <dl className="space-y-4 text-foreground">
            <div className="flex items-center gap-2">
              <Clock4Icon aria-hidden="true" />
              <dd>{service.duration}</dd>
            </div>
          </dl>

          <Button
            onClick={handleStartService}
            className="mt-8 w-full rounded-full h-10"
            aria-label={t('serviceDetails.startNow')}
          >
            {t('serviceDetails.startNow')}
          </Button>
        </aside>
      </main>
    </>
  );
}
